import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { DownIcon } from '@trello/nachos/icons/down';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { UnlockedIcon } from '@trello/nachos/icons/unlocked';
import { IconRedColor } from '@trello/nachos/icon';
import { token } from '@trello/theme';
import { CreateTeamVisibilityTestIds } from '@trello/test-ids';
import { Popover, usePopover } from '@trello/nachos/popover';
import { WorkspaceSettingsVisibilityPopoverContentComponent } from 'app/src/components/WorkspaceSettings';
import type { FunctionComponent, ComponentProps } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import styles from './CreateTeamVisibility.less';

const format = forTemplate('create_team');

export type PermissionLevels = 'private' | 'domain';
type Domain = ComponentProps<
  typeof WorkspaceSettingsVisibilityPopoverContentComponent
>['domain'];

interface CreateTeamVisibilityProps {
  domain: Domain;
  onChange?: (newVisibility: PermissionLevels) => void;
}

export const CreateTeamVisibility: FunctionComponent<CreateTeamVisibilityProps> = ({
  domain,
  onChange,
}) => {
  const visibilityIcons = useMemo(
    () => ({
      private: (
        <PrivateIcon
          color={token('color.icon.accent.red', IconRedColor)}
          size="small"
        />
      ),
      domain: <UnlockedIcon size="small" />,
    }),
    [],
  );
  const {
    triggerRef,
    toggle,
    popoverProps,
    hide,
  } = usePopover<HTMLButtonElement>();
  const [currentVisibility, setCurrentVisibility] = useState<PermissionLevels>(
    'domain',
  );

  const onUpdateVisibility = useCallback(
    (visibility: PermissionLevels) => {
      setCurrentVisibility(visibility);
      hide();
      onChange && onChange(visibility);
    },
    [hide, setCurrentVisibility, onChange],
  );

  return (
    <>
      <div className={styles.createTeamVisibility}>
        <label htmlFor="createTeamVisibility">
          {format('visibility-header')}
        </label>
        <Button
          ref={triggerRef}
          name="createTeamVisibility"
          size="fullwidth"
          iconAfter={<DownIcon size="small" />}
          onClick={toggle}
        >
          <span
            className={styles.selectText}
            data-testid={CreateTeamVisibilityTestIds.SelectText}
          >
            {visibilityIcons[currentVisibility]}
            {format(`visibility-${currentVisibility}-title`)}
          </span>
        </Button>
        <p>{format(`visibility-${currentVisibility}-description`)}</p>
      </div>
      <Popover noHorizontalPadding {...popoverProps}>
        <WorkspaceSettingsVisibilityPopoverContentComponent
          domain={domain}
          // @ts-expect-error
          onChange={onUpdateVisibility}
          currentVisibility={currentVisibility}
          disabledOptions={['public']}
        />
      </Popover>
    </>
  );
};
